import { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "@/components/Sidebar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { 
  ShoppingCart, Plus, Minus, Trash2, Search, IndianRupee, Package, 
  Loader2, CheckCircle, MapPin, Phone, AlertCircle, Wallet, AlertTriangle 
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderingPage({ user }) {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  
  // Wallet state
  const [wallet, setWallet] = useState({
    balance: 0,
    low_balance_warning: false,
    threshold: 500
  });
  
  // Delivery address state
  const [deliveryAddress, setDeliveryAddress] = useState({
    name: user.name || "",
    phone: user.phone || "",
    address_line1: user.address_line1 || "",
    address_line2: user.address_line2 || "",
    city: user.city || "",
    state: user.state || "",
    pincode: user.pincode || ""
  });
  const [orderNotes, setOrderNotes] = useState("");

  const { 
    cart, 
    addToCart, 
    removeFromCart, 
    updateQuantity, 
    getItemQuantity, 
    clearCart, 
    cartTotal, 
    cartCount 
  } = useCart();

  useEffect(() => {
    fetchProducts();
    fetchWalletBalance();
  }, []);

  // Update delivery address when user data changes
  useEffect(() => {
    setDeliveryAddress({
      name: user.name || "",
      phone: user.phone || "",
      address_line1: user.address_line1 || "",
      address_line2: user.address_line2 || "",
      city: user.city || "",
      state: user.state || "",
      pincode: user.pincode || ""
    });
  }, [user]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`);
      setWallet(response.data);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    }
  };

  const categories = ["All", ...new Set(products.map(p => p.category))];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddToCart = (product) => {
    addToCart(product);
    toast.success(`${product.name} added to cart`);
  };

  const openCheckout = () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setIsCartOpen(false);
    setIsCheckoutOpen(true);
  };

  const isAddressComplete = () => {
    return deliveryAddress.name && 
           deliveryAddress.phone && 
           deliveryAddress.address_line1 && 
           deliveryAddress.city && 
           deliveryAddress.state && 
           deliveryAddress.pincode;
  };

  const hasInsufficientBalance = cartTotal > wallet.balance;

  const placeOrder = async () => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!isAddressComplete()) {
      toast.error("Please fill in all required address fields");
      return;
    }

    if (hasInsufficientBalance) {
      toast.error("Insufficient wallet balance. Please contact admin to add funds.");
      return;
    }

    setIsPlacingOrder(true);
    try {
      const orderItems = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price
      }));

      // Create order in backend (payment from wallet)
      const response = await axios.post(`${API}/orders`, { 
        items: orderItems,
        delivery_address: deliveryAddress,
        notes: orderNotes || null
      });
      
      toast.success("Order placed successfully! Payment deducted from wallet.");
      clearCart();
      setIsCheckoutOpen(false);
      setOrderNotes("");
      
      // Refresh wallet balance
      fetchWalletBalance();
    } catch (error) {
      console.error("Error placing order:", error);
      const errorMessage = error.response?.data?.detail || "Failed to place order";
      toast.error(errorMessage);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      <Sidebar user={user} activePath="/ordering" />
      
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64 pb-24 lg:pb-8 overflow-x-hidden max-w-full">
        {/* Header with Wallet Balance */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground mb-1">Order Supplies</h1>
            <p className="text-muted-foreground text-sm sm:text-base">Browse and order from our product catalog</p>
          </div>
          
          {/* Wallet Balance Card */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Card className={`border-0 shadow-md ${wallet.low_balance_warning ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50'}`}>
              <CardContent className="p-3 sm:p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${wallet.low_balance_warning ? 'bg-yellow-100' : 'bg-green-100'}`}>
                  <Wallet className={`w-5 h-5 ${wallet.low_balance_warning ? 'text-yellow-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Wallet Balance</p>
                  <p className={`font-heading text-xl flex items-center ${wallet.low_balance_warning ? 'text-yellow-700' : 'text-green-700'}`}>
                    <IndianRupee className="w-4 h-4" />
                    {wallet.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Cart Button - Fixed on mobile */}
            <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
              <SheetTrigger asChild>
                <Button 
                  data-testid="cart-btn"
                  className="fixed bottom-20 right-4 lg:relative lg:bottom-auto lg:right-auto z-40 rounded-full shadow-lg lg:shadow-button h-14 lg:h-10 px-6"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Cart
                  {cartCount > 0 && (
                    <span className="ml-2 bg-white text-primary rounded-full px-2 py-0.5 text-sm font-bold">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg" data-testid="cart-drawer">
                <SheetHeader>
                  <SheetTitle className="font-heading text-2xl">Your Cart</SheetTitle>
                </SheetHeader>
                
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                    <ShoppingCart className="w-16 h-16 mb-4 opacity-30" />
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
                    <div className="flex-1 overflow-auto space-y-4">
                      {cart.map((item) => (
                        <div 
                          key={item.product_id}
                          className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl"
                          data-testid={`cart-item-${item.product_id}`}
                        >
                          <img 
                            src={item.image_url || "https://via.placeholder.com/80"} 
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate">{item.name}</h4>
                            <p className="text-sm text-muted-foreground flex items-center">
                              <IndianRupee className="w-3 h-3" />
                              {item.price} × {item.quantity} = <IndianRupee className="w-3 h-3 ml-1" />{item.price * item.quantity}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-2">
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateQuantity(item.product_id, -1)}
                              data-testid={`cart-decrease-${item.product_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-6 text-center font-semibold text-sm">{item.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateQuantity(item.product_id, 1)}
                              data-testid={`cart-increase-${item.product_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => removeFromCart(item.product_id)}
                              data-testid={`cart-remove-${item.product_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Cart Footer with Wallet Info */}
                    <div className="border-t pt-4 mt-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Cart Total</span>
                        <span className="font-heading text-xl flex items-center">
                          <IndianRupee className="w-4 h-4" />
                          {cartTotal.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Wallet Balance</span>
                        <span className={`font-semibold flex items-center ${hasInsufficientBalance ? 'text-red-500' : 'text-green-600'}`}>
                          <IndianRupee className="w-4 h-4" />
                          {wallet.balance.toLocaleString('en-IN')}
                        </span>
                      </div>
                      
                      {hasInsufficientBalance && (
                        <Alert className="border-red-200 bg-red-50">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <AlertDescription className="text-red-700 text-sm">
                            Insufficient balance! Need ₹{(cartTotal - wallet.balance).toLocaleString('en-IN')} more.
                          </AlertDescription>
                        </Alert>
                      )}
                      
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="font-semibold">Balance After Order</span>
                        <span className={`font-heading text-xl flex items-center ${hasInsufficientBalance ? 'text-red-500' : 'text-green-600'}`}>
                          <IndianRupee className="w-4 h-4" />
                          {Math.max(0, wallet.balance - cartTotal).toLocaleString('en-IN')}
                        </span>
                      </div>
                      
                      <Button 
                        className="w-full h-12 rounded-full shadow-button"
                        onClick={openCheckout}
                        disabled={hasInsufficientBalance}
                        data-testid="proceed-checkout-btn"
                      >
                        <Wallet className="w-5 h-5 mr-2" />
                        {hasInsufficientBalance ? 'Insufficient Balance' : 'Proceed to Checkout'}
                      </Button>
                    </div>
                  </div>
                )}
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Low Balance Warning */}
        {wallet.low_balance_warning && (
          <Alert className="mb-6 border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Low wallet balance!</strong> Your balance is below ₹{wallet.threshold}. Please contact admin to add funds.
            </AlertDescription>
          </Alert>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="search-input"
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 rounded-xl"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(category => (
              <Badge
                key={category}
                data-testid={`category-${category}`}
                variant={selectedCategory === category ? "default" : "outline"}
                className={`cursor-pointer rounded-full px-4 py-2 whitespace-nowrap transition-all ${
                  selectedCategory === category 
                    ? 'bg-gradient-to-r from-[#F2337B] to-[#FF6B9D] border-0' 
                    : 'hover:bg-[#F2337B]/10'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="spinner"></div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <h3 className="text-xl font-semibold mb-2">No products found</h3>
            <p className="text-muted-foreground">Try a different search term or category</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {filteredProducts.map(product => {
              const quantity = getItemQuantity(product.product_id);
              return (
                <Card 
                  key={product.product_id} 
                  className="product-card group border-0"
                  data-testid={`product-card-${product.product_id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="relative mb-3">
                      <img 
                        src={product.image_url || "https://via.placeholder.com/200"} 
                        alt={product.name}
                        className="w-full h-28 sm:h-36 lg:h-40 object-cover rounded-xl"
                      />
                      {!product.in_stock && (
                        <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center">
                          <span className="text-white font-semibold">Out of Stock</span>
                        </div>
                      )}
                      <Badge className={`absolute top-2 left-2 rounded-full text-xs category-${product.category.toLowerCase()}`}>
                        {product.category}
                      </Badge>
                    </div>
                    
                    <h3 className="font-semibold text-sm sm:text-base mb-1 line-clamp-1">{product.name}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-3 line-clamp-2 h-8 sm:h-10">{product.description}</p>
                    
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-heading text-lg sm:text-xl flex items-center text-[#F2337B]">
                        <IndianRupee className="w-4 h-4" />
                        {product.price}
                      </span>
                      
                      {product.in_stock ? (
                        quantity > 0 ? (
                          <div className="flex items-center gap-1 sm:gap-2">
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateQuantity(product.product_id, -1)}
                              data-testid={`decrease-${product.product_id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-6 text-center font-semibold">{quantity}</span>
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="h-8 w-8 rounded-full"
                              onClick={() => updateQuantity(product.product_id, 1)}
                              data-testid={`increase-${product.product_id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            size="sm"
                            className="rounded-full text-xs sm:text-sm h-8 px-3"
                            onClick={() => handleAddToCart(product)}
                            data-testid={`add-to-cart-${product.product_id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        )
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground text-xs">
                          Unavailable
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Checkout Dialog */}
      <Dialog open={isCheckoutOpen} onOpenChange={setIsCheckoutOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Checkout</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Wallet Payment Info */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Wallet Payment</p>
                  <p className="text-sm text-green-600">Amount will be deducted from your wallet</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Order Total</span>
                  <span className="font-semibold text-green-800">₹{cartTotal.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Wallet Balance</span>
                  <span className="font-semibold text-green-800">₹{wallet.balance.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-green-200">
                  <span className="font-semibold text-green-800">Balance After Order</span>
                  <span className="font-bold text-green-900">₹{(wallet.balance - cartTotal).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#F2337B]" />
                Delivery Address
              </h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Name *</Label>
                    <Input 
                      value={deliveryAddress.name}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, name: e.target.value})}
                      placeholder="Full name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone *</Label>
                    <Input 
                      value={deliveryAddress.phone}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, phone: e.target.value})}
                      placeholder="Phone number"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Address Line 1 *</Label>
                  <Input 
                    value={deliveryAddress.address_line1}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, address_line1: e.target.value})}
                    placeholder="Street address"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Address Line 2</Label>
                  <Input 
                    value={deliveryAddress.address_line2}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, address_line2: e.target.value})}
                    placeholder="Apartment, suite, etc. (optional)"
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">City *</Label>
                    <Input 
                      value={deliveryAddress.city}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                      placeholder="City"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">State *</Label>
                    <Input 
                      value={deliveryAddress.state}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})}
                      placeholder="State"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Pincode *</Label>
                    <Input 
                      value={deliveryAddress.pincode}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, pincode: e.target.value})}
                      placeholder="Pincode"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Notes */}
            <div>
              <Label className="text-xs">Order Notes (Optional)</Label>
              <Textarea 
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Any special instructions..."
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Order Summary */}
            <div className="p-4 bg-muted rounded-xl">
              <h4 className="font-semibold mb-2">Order Summary</h4>
              <div className="space-y-2 text-sm max-h-32 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.product_id} className="flex justify-between">
                    <span className="truncate max-w-[200px]">{item.name} × {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-semibold mt-3 pt-3 border-t">
                <span>Total</span>
                <span className="text-lg">₹{cartTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {!isAddressComplete() && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800 text-sm">
                  Please fill in all required address fields marked with *
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsCheckoutOpen(false)}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={placeOrder}
              disabled={isPlacingOrder || !isAddressComplete() || hasInsufficientBalance}
              className="rounded-full bg-gradient-to-r from-[#2ECC71] to-[#27AE60]"
              data-testid="place-order-btn"
            >
              {isPlacingOrder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Pay ₹{cartTotal.toLocaleString('en-IN')} from Wallet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
